import './WidgetContainer.scss';
import cn from 'classnames';
import { KeyboardEvent, PropsWithChildren, ReactNode, Ref, useMemo, useState } from 'react';
import { MdArrowForwardIos } from 'react-icons/md';
import { Background, BackgroundType } from '../designConstants';

interface WidgetContainerProps extends PropsWithChildren {
  background?: BackgroundType;
  collapsible?: boolean;
  disabled?: boolean;
  footer?: string | ReactNode;
  header?: string | ReactNode;
  headerCollapsibleOnClick?: boolean;
  rightActions?: string | ReactNode;
  startOpen?: boolean;
  subHeader?: string | ReactNode;
  className?: string;
  dataTestId?: string;
  includeDivider?: boolean;
  containerRef?: Ref<HTMLDivElement>; // Exposes the root node, e.g. for a dev-mode layout check (see ModalEditWidget)
  // Stretches the widget (and its body) to fill its own container's height, instead of sizing
  // to content. Off by default since most widgets sit in a normal document flow and should size
  // to their content -- opt in when the widget's parent already constrains a height and its
  // content (e.g. a table) should fill it, same as ModalEditWidget builds for itself.
  fillHeight?: boolean;
}

export const WidgetContainer = ({
  background = Background.LIGHT,
  footer,
  children,
  collapsible = false,
  disabled = false,
  header,
  headerCollapsibleOnClick = false,
  rightActions,
  startOpen = true,
  subHeader,
  className,
  dataTestId,
  includeDivider = false,
  containerRef,
  fillHeight = false
}: WidgetContainerProps) => {
  const [isOpen, setIsOpen] = useState(startOpen);

  // Ensure only one of the arrow or the header gets an onClick to collapse/expand the widget
  const headerInteractiveAttributes = useMemo(() => {
    if (disabled || !collapsible || !headerCollapsibleOnClick) {
      return {};
    }

    return {
      'aria-expanded': isOpen,
      onClick: () => {
        setIsOpen(prev => !prev);
      },
      onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          setIsOpen(prev => !prev);
        }
      },
      role: 'button',
      tabIndex: 0
    };
  }, [collapsible, disabled, headerCollapsibleOnClick, isOpen]);

  const handleArrowToggle = useMemo(() => {
    if (disabled || !collapsible || headerCollapsibleOnClick) {
      return undefined;
    }

    return () => {
      setIsOpen(prev => !prev);
    };
  }, [collapsible, disabled, headerCollapsibleOnClick]);

  const hasHeaderRow = Boolean(header || subHeader || rightActions || collapsible);

  return (
    <div
      ref={containerRef}
      className={cn('widget-container', className, background, {
        open: isOpen,
        closed: !isOpen,
        disabled,
        'fill-height': fillHeight,
        'no-header': !hasHeaderRow
      })}
      data-testid={dataTestId}
    >
      {hasHeaderRow && (
        <div className='header-row' {...headerInteractiveAttributes}>
          <div className='header-left'>
            {header && <div className='title'>{header}</div>}
            {isOpen && subHeader && (
              <div className='subheader'>
                <div className='text'>{subHeader}</div>
              </div>
            )}
          </div>
          {((rightActions && isOpen) || collapsible) && (
            <div className='header-right'>
              {rightActions && isOpen && <div className='right-actions'>{rightActions}</div>}
              {collapsible && (
                <div className='open-close-arrow'>
                  <MdArrowForwardIos onClick={handleArrowToggle} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {includeDivider && <div className='divider' />}
      <div className={cn('widget', { 'widget-collapsible': collapsible, closed: !isOpen })}>
        <div className={cn('widget-inner', { 'widget-collapsible-inner': collapsible })}>
          <div className='widget-body'>{children}</div>
          {footer && (
            <>
              <div className='divider' />
              <div className='widget-footer'>{footer}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
